import { useEffect, useMemo, useState } from "react"
import dayjs from "dayjs"
import { toast } from "react-toastify"
import { XMarkIcon } from "@heroicons/react/24/outline"
import { Button } from "~/ui-rtk/components/ui/controls"
import { Loader } from "~/ui-rtk/components/ui/common"
import {
  useApplyMegaFeedStructureRunMutation,
  useGetMegaFeedStructureRunQuery,
  useSetMegaFeedStructureRunItemMutation,
  type MegaFeedMediaType,
  type MegaFeedRunItemStatus,
  type MegaFeedStructure,
  type MegaFeedStructureRunItem,
} from "~/ui-rtk/api/megaFeedStructureApi"
import LibraryAssetPicker from "./LibraryAssetPicker"

const POLL_MS = 3000

const STATUS_STYLE: Record<MegaFeedRunItemStatus, string> = {
  PENDING: "border-basic-blue text-cadet-blue",
  GENERATING: "border-system-blue-600 text-basic-white",
  MATCHED: "border-green-500 text-green-400",
  GENERATED: "border-fuchsia-500 text-fuchsia-300",
  UNFILLED: "border-yellow-600 text-yellow-400",
  FAILED: "border-red-600 text-red-400",
}

const STATUS_LABEL: Record<MegaFeedRunItemStatus, string> = {
  PENDING: "Waiting",
  GENERATING: "Generating",
  MATCHED: "Matched",
  GENERATED: "Generated",
  UNFILLED: "Missing",
  FAILED: "Failed",
}

const GENERATED_FROM = {
  recolor: "recolored from a sibling colorway",
  generate: "generated from a product photo",
}

/** Where a reused (MATCHED) image came from. */
function sourceLabel(item: MegaFeedStructureRunItem): string | null {
  const meta = item.metadata
  switch (meta?.source) {
    case "classifier":
      return "reused: classified product photo"
    case "snapshot":
      return `reused from the feed snapshot${meta.detail ? `: ${meta.detail}` : ""}`
    case "tagged":
      return meta.detail === item.productRefId
        ? "reused: tagged to this colorway"
        : "reused: tagged to this product"
    case "shopify":
      return "reused: store (Shopify) image"
    default:
      return null
  }
}

/** The cell tooltip for a MATCHED image: picked, closest fallback, or its source. */
function reusedFrom(
  item: MegaFeedStructureRunItem,
  requestedShot: string,
): string | null {
  const meta = item.metadata
  if (meta?.source === "manual") return "picked by you"
  const from = sourceLabel(item)
  if (meta?.selectedBy === "ai") {
    const picked = `picked by AI${meta.reason ? `: ${meta.reason}` : ""}`
    return from ? `${picked} (${from})` : picked
  }
  if (!meta?.fallback) return from
  const closest = `closest existing image (no ${requestedShot} found)`
  return from ? `${closest}, ${from}` : closest
}

type Props = {
  runId: string
  structure?: MegaFeedStructure
  /** ref → what to call it in the grid (product title · color). */
  refLabels: Record<string, string>
  onDone: () => void
}

function RunCell({
  item,
  requestedShot,
  isEditable,
  isSaving,
  onPick,
  onClear,
}: {
  item: MegaFeedStructureRunItem
  requestedShot: string
  isEditable: boolean
  isSaving: boolean
  onPick: () => void
  onClear: () => void
}) {
  const isWorking =
    isSaving || item.status === "GENERATING" || item.status === "PENDING"
  const isPicked = item.metadata?.source === "manual"
  const title = [
    STATUS_LABEL[item.status],
    item.status === "GENERATED"
      ? GENERATED_FROM[item.metadata?.mode ?? "recolor"]
      : null,
    item.status === "MATCHED" ? reusedFrom(item, requestedShot) : null,
    item.error,
    isEditable ? "click to pick another image" : null,
  ]
    .filter(Boolean)
    .join(": ")
  return (
    <div
      title={title}
      role={isEditable ? "button" : undefined}
      aria-label={
        isEditable ? `Pick image for ${item.productRefId}` : undefined
      }
      tabIndex={isEditable ? 0 : undefined}
      onClick={isEditable && !isSaving ? onPick : undefined}
      onKeyDown={
        isEditable && !isSaving
          ? e => {
              if (e.key === "Enter" || e.key === " ") onPick()
            }
          : undefined
      }
      className={`group relative mx-auto w-14 h-14 rounded-md border-2 overflow-hidden flex items-center justify-center bg-basic-dark-blue ${STATUS_STYLE[item.status]} ${isEditable ? "cursor-pointer hover:brightness-125" : ""}`}
    >
      {item.assetUrl && (
        <img
          src={item.assetUrl}
          alt={STATUS_LABEL[item.status]}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      )}
      {isWorking && (
        <span
          className={`loading loading-spinner loading-xs ${item.assetUrl ? "absolute text-white" : ""}`}
        />
      )}
      {!item.assetUrl && !isWorking && (
        <span className="text-[10px] leading-tight px-1">
          {STATUS_LABEL[item.status]}
        </span>
      )}
      {item.status === "GENERATED" && (
        <span className="absolute bottom-0 inset-x-0 bg-fuchsia-600/80 text-[9px] text-white">
          AI
        </span>
      )}
      {isPicked && item.assetUrl && (
        <span className="absolute bottom-0 inset-x-0 bg-brand/80 text-[9px] text-white">
          Picked
        </span>
      )}
      {isEditable && item.assetId && !isSaving && (
        <button
          type="button"
          aria-label="Clear image"
          title="Empty this cell"
          onClick={e => {
            e.stopPropagation()
            onClear()
          }}
          className="absolute top-0 right-0 hidden group-hover:flex h-4 w-4 items-center justify-center rounded-bl bg-black/70 text-white"
        >
          <XMarkIcon className="h-3 w-3" />
        </button>
      )}
    </div>
  )
}

/**
 * A structure run as a ref × slot grid. Polls while the job fills it; the
 * result only reaches the feed when the user applies it.
 */
export default function StructureRunView({
  runId,
  structure,
  refLabels,
  onDone,
}: Props) {
  const [isOpen, setIsOpen] = useState(true)
  // Polls until the run settles (completed / failed / cancelled).
  const { data: run } = useGetMegaFeedStructureRunQuery(runId, {
    pollingInterval: isOpen ? POLL_MS : 0,
    skipPollingIfUnfocused: true,
  })
  useEffect(() => {
    if (run) setIsOpen(run.status === "PENDING" || run.status === "RUNNING")
  }, [run])
  const [applyRun, { isLoading: isApplying, isSuccess: isApplied }] =
    useApplyMegaFeedStructureRunMutation()
  const [setItem] = useSetMegaFeedStructureRunItemMutation()
  // The cell whose image the library picker is choosing, and the one saving.
  const [picking, setPicking] = useState<MegaFeedStructureRunItem | null>(null)
  const [savingItemId, setSavingItemId] = useState<string | null>(null)

  const saveItem = async (
    item: MegaFeedStructureRunItem,
    assetId: string | null,
  ) => {
    setSavingItemId(item.id)
    try {
      await setItem({ runId, itemId: item.id, assetId }).unwrap()
      if (run?.appliedAt) {
        toast.success("Saved. The structure's feed link already serves it.")
      }
    } catch {
      toast.error("Couldn't save the image, try again.")
    } finally {
      setSavingItemId(null)
    }
  }

  const { rows, counts, done, total } = useMemo(() => {
    const byRef = new Map<string, MegaFeedStructureRunItem[]>()
    const tally: Partial<Record<MegaFeedRunItemStatus, number>> = {}
    for (const item of run?.items ?? []) {
      byRef.set(item.productRefId, [
        ...(byRef.get(item.productRefId) ?? []),
        item,
      ])
      tally[item.status] = (tally[item.status] ?? 0) + 1
    }
    const items = run?.items.length ?? 0
    const open = (tally.PENDING ?? 0) + (tally.GENERATING ?? 0)
    return {
      rows: [...byRef.entries()],
      counts: tally,
      done: items - open,
      total: items,
    }
  }, [run])

  // The structure may have been edited since an older run: show its slots
  // and the run's, so no filled item is hidden.
  const slotPositions = useMemo(
    () =>
      [
        ...new Set([
          ...(structure?.slots.map(s => s.position) ?? []),
          ...(run?.items.map(i => i.slotPosition) ?? []),
        ]),
      ].sort((a, b) => a - b),
    [structure, run],
  )
  const slotTitle = (position: number) => {
    const slot = structure?.slots.find(s => s.position === position)
    return (
      slot?.label || slot?.criterion || slot?.kind || `Slot ${position + 1}`
    )
  }
  const slotMediaType = (position: number): MegaFeedMediaType =>
    structure?.slots.find(s => s.position === position)?.mediaType ?? "IMAGE"
  const isEditable = run?.status === "COMPLETED"

  const filled = (counts.MATCHED ?? 0) + (counts.GENERATED ?? 0)
  const aiPicked =
    run?.items.filter(
      i => i.status === "MATCHED" && i.metadata?.selectedBy === "ai",
    ).length ?? 0
  // A run published before can be published again: that rolls the link back to it.
  let publishLabel = "Publish to feed link"
  if (run?.appliedAt) publishLabel = "Publish again"
  if (isApplied) publishLabel = "Published"
  if (isApplying) publishLabel = "Publishing…"

  const apply = async () => {
    try {
      const result = await applyRun(runId).unwrap()
      toast.success(
        `Published ${result.assets} assets for ${result.refs} products. Meta reads this structure's feed link on its next fetch.`,
      )
      onDone()
    } catch {
      toast.error("Couldn't publish the run, try again.")
    }
  }

  if (!run) {
    return (
      <div className="flex justify-center p-6">
        <Loader />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-system-sm">
      <div className="flex items-center justify-between gap-system-sm flex-wrap">
        <div className="text-sm text-basic-white">
          {isOpen && (
            <span>
              Filling… {done}/{total} slots
            </span>
          )}
          {!isOpen && run.status === "COMPLETED" && (
            <span>
              {filled}/{total} slots filled · {aiPicked} AI-picked from the
              library · {(counts.MATCHED ?? 0) - aiPicked} other reused ·{" "}
              {counts.GENERATED ?? 0} AI generated · {counts.UNFILLED ?? 0}{" "}
              missing
              {counts.FAILED ? ` · ${counts.FAILED} failed` : ""}
            </span>
          )}
          {!isOpen && run.status !== "COMPLETED" && (
            <span className="text-red-400">
              Run {run.status.toLowerCase()}
              {run.error ? `: ${run.error}` : ""}
            </span>
          )}
        </div>
        {isOpen && (
          <progress
            className="progress progress-primary w-48"
            value={done}
            max={Math.max(total, 1)}
          />
        )}
      </div>

      <div className="max-h-[55vh] overflow-auto border border-basic-blue rounded-lg">
        <table className="text-xs w-full">
          <thead className="sticky top-0 bg-basic-dark-blue z-10">
            <tr>
              <th className="text-left p-2 text-cadet-blue font-medium min-w-[180px]">
                Product
              </th>
              {slotPositions.map(position => (
                <th
                  key={position}
                  className="p-2 text-cadet-blue font-medium text-center whitespace-nowrap"
                >
                  {position + 1}. {slotTitle(position)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(([ref, items]) => (
              <tr key={ref} className="border-t border-basic-blue">
                <td className="p-2 text-basic-white">
                  {refLabels[ref] ?? ref}
                </td>
                {slotPositions.map(position => {
                  const item = items.find(i => i.slotPosition === position)
                  return (
                    <td key={position} className="p-1 text-center">
                      {item ? (
                        <RunCell
                          item={item}
                          requestedShot={slotTitle(position)}
                          isEditable={isEditable}
                          isSaving={savingItemId === item.id}
                          onPick={() => setPicking(item)}
                          onClear={() => void saveItem(item, null)}
                        />
                      ) : null}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isEditable && (
        <div className="text-xs text-cadet-blue">
          Click a cell to pick a different image from the library.
        </div>
      )}

      <LibraryAssetPicker
        isOpen={picking !== null}
        mediaType={picking ? slotMediaType(picking.slotPosition) : "IMAGE"}
        title={
          picking
            ? `${refLabels[picking.productRefId] ?? picking.productRefId}: ${slotTitle(picking.slotPosition)}`
            : ""
        }
        // A color ref `<productId>_<color>` belongs to its product's family.
        productId={picking?.productRefId.split("_")[0]}
        onClose={() => setPicking(null)}
        onSelect={asset => {
          if (picking) void saveItem(picking, asset.id)
        }}
      />

      <div className="flex items-center justify-end gap-system-sm">
        {run.appliedAt && (
          <span className="text-xs text-green-400">
            Published {dayjs(run.appliedAt).format("MMM D, YYYY HH:mm")}
          </span>
        )}
        <span className="text-xs text-cadet-blue">
          Publishing serves this run on the structure&apos;s feed link;
          publishing an older run again rolls back to it.
        </span>
        <Button variant={{ variant: "outlined" }} size="sm" onClick={onDone}>
          Close
        </Button>
        <Button
          size="sm"
          onClick={() => void apply()}
          disabled={run.status !== "COMPLETED" || filled === 0 || isApplied}
          isLoading={isApplying}
        >
          {publishLabel}
        </Button>
      </div>
    </div>
  )
}
